import { useLazyPostCall, useLazyPutCall } from 'common/api/useApiCall';
import { NotificationContext } from 'common/NotificationContainer/NotificationContainer';
import { evaluateTenantName } from 'common/TenantContainer/evaluateTenantName';
import { TENANTS_BASE_URL, URLS } from 'constants/URL';
import { OTPRequestType } from 'constants/URLEnums';
import { TenantType } from 'customers/tenants-common-view/lib/schema/Tenant.types';
import { useContext, useEffect, useMemo, useState } from 'react';
import { isValidURL } from './helpers';

interface UpdateOngoingTenantIssueProps {
  selectedTenant: TenantType;
  onSuccess: () => void;
}

export const TENANT_ONGOING_ISSUE_FORM_UPDATE_IDENTIFIERS = {
  ISSUE: 'note',
  LINK: 'link',
};

const MINIMUM_ISSUE_LENGTH = 50;

const useUpdateOngoingTenantIssue = (props: UpdateOngoingTenantIssueProps) => {
  const { selectedTenant, onSuccess } = props;
  const tenantOngoingIssue = selectedTenant?.tenantOngoingIssue;
  const { addNotification } = useContext(NotificationContext);

  const [step, setStep] = useState(1);
  const [uuid, setUuid] = useState('');
  const [openOTP, setOpenOTP] = useState(false);

  const [form, setForm] = useState({
    [TENANT_ONGOING_ISSUE_FORM_UPDATE_IDENTIFIERS.ISSUE]:
      tenantOngoingIssue?.note || '',
    [TENANT_ONGOING_ISSUE_FORM_UPDATE_IDENTIFIERS.LINK]:
      tenantOngoingIssue?.link || '',
  });

  const updateForm = (key: string, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const clearIssue = () => {
    setForm(null);
  };

  const {
    response: sendOTPRes,
    isLoading: sendOTPIsLoading,
    postData: sendOTP,
  } = useLazyPostCall(
    `${URLS.otp}purpose=${OTPRequestType.TENANT_ONGOING_ISSUE_UPDATE_OTP}`,
  );

  useEffect(() => {
    if (sendOTPRes?.uuid) {
      setUuid(sendOTPRes?.uuid);
      setStep(2);
    }
  }, [sendOTPRes]);

  const {
    response: updateOngoingIssueResponse,
    postData: updateOngoingIssue,
    isLoading: updateOngoingIssueIsLoading,
    responseStatus: updateOngoingIssueResponseStatus,
  } = useLazyPutCall(
    `${TENANTS_BASE_URL}/${selectedTenant?._id}/issue`,
    null,
    true,
  );

  const sendOTPCall = ({ reason, jiraId }) => {
    const payload = {
      tenantId: selectedTenant?._id,
      tenantDomain: selectedTenant?.pgSource,
      comment: reason,
      jiraId: jiraId,
      tenantName: evaluateTenantName({ tenant: selectedTenant }),
      otpPurpose: OTPRequestType.TENANT_ONGOING_ISSUE_UPDATE_OTP,
    };
    sendOTP(JSON.stringify(payload, null));
  };

  const onSubmit = (val) => {
    if (step === 1) {
      sendOTPCall({
        reason: val?.reason,
        jiraId: val?.jiraId,
      });
    } else if (step === 2) {
      const payload = {
        tenantId: selectedTenant?._id,

        otpPayload: {
          requestId: uuid,
          otp: val,
        },
        tenantOngoingIssue: form,
      };
      updateOngoingIssue(JSON.stringify(payload, null, 2));
    }
  };

  useEffect(() => {
    if (updateOngoingIssueResponseStatus === 200) {
      addNotification({
        severity: 'success',
        message: `Update Successful`,
      });
      onSuccess();
    } else if (updateOngoingIssueResponse) {
      addNotification({
        severity: 'error',
        message:
          updateOngoingIssueResponse?.message ||
          'Something went wrong, please try again.',
      });
    }
    setOpenOTP(false);
    setStep(1);
  }, [updateOngoingIssueResponse]);

  const disableNext = useMemo(() => {
    if (step === 1)
      return (
        form?.[TENANT_ONGOING_ISSUE_FORM_UPDATE_IDENTIFIERS.ISSUE]?.length <
          MINIMUM_ISSUE_LENGTH ||
        !isValidURL(form?.[TENANT_ONGOING_ISSUE_FORM_UPDATE_IDENTIFIERS.LINK])
      );
    return false;
  }, [step, form]);

  const disableClear = selectedTenant?.tenantOngoingIssue === null;

  const isNoteValid = useMemo(
    () =>
      form?.[TENANT_ONGOING_ISSUE_FORM_UPDATE_IDENTIFIERS.ISSUE]?.length >=
      MINIMUM_ISSUE_LENGTH,
    [form?.[TENANT_ONGOING_ISSUE_FORM_UPDATE_IDENTIFIERS.ISSUE]],
  );

  return {
    step,
    openOTP,
    setOpenOTP,
    sendOTPIsLoading,
    sendOTP,
    onSubmit,
    form,
    updateForm,
    updateOngoingIssueIsLoading,
    clearIssue,
    disableNext,
    disableClear,
    isNoteValid,
  };
};

export default useUpdateOngoingTenantIssue;
